import { createButton } from "@gluestack-ui/core/button/creator";
import { PrimitiveIcon, UIIcon } from "@gluestack-ui/core/icon/creator";
import { VariantProps, tva, useStyleContext, withStyleContext } from "@gluestack-ui/utils";
import { cssInterop } from "nativewind";
import React from "react";
import { Pressable, Text, View } from "react-native";
import { Spinner } from "../Spinner/Spinner";

const SCOPE = "BUTTON";

const Root = withStyleContext(Pressable, SCOPE);

const UIButton = createButton({
	Root: Root,
	Text,
	Group: View,
	Spinner: Spinner,
	Icon: UIIcon,
});

cssInterop(PrimitiveIcon, {
	className: {
		target: "style",
		nativeStyleToProp: {
			height: true,
			width: true,
			fill: true,
			color: "classNameColor",
			stroke: true,
		} as any,
	},
});

const buttonStyle = tva({
	base: "rounded-lg flex-row items-center justify-center data-[focus-visible=true]:web:outline-none data-[focus-visible=true]:web:ring-2 data-[disabled=true]:opacity-40 gap-2 border border-neutral-950",
	variants: {
		action: {
			primary:
				"bg-primary-500 data-[hover=true]:bg-primary-400 data-[active=true]:bg-primary-300 data-[focus-visible=true]:web:ring-indicator-info",
			secondary:
				"bg-neutral-0 data-[hover=true]:bg-neutral-100 data-[active=true]:bg-neutral-200 data-[focus-visible=true]:web:ring-indicator-info",
			inverse: "",
		},
		variant: {
			link: "px-0 rounded-none border-0",
			outline: "bg-transparent data-[hover=true]:bg-primary-50 data-[active=true]:bg-transparent",
			solid: "",
		},
		size: {
			xs: "px-3.5 h-9",
			sm: "px-4 h-10",
			md: "px-5 h-12",
			lg: "px-6 h-14 rounded-xl",
			xl: "px-7 h-16 rounded-xl",
		},
		disabled: {
			true: "opacity-40",
		},
	},
	compoundVariants: [
		{
			action: "primary",
			variant: "link",
			class: "px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
		},
		{
			action: "secondary",
			variant: "link",
			class: "px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent",
		},
		{
			action: "secondary",
			variant: "solid",
			class: "bg-neutral-0",
		},
		{
			action: "primary",
			variant: "outline",
			class: "bg-transparent data-[hover=true]:bg-primary-50 data-[active=true]:bg-transparent",
		},
		{
			action: "secondary",
			variant: "outline",
			class: "bg-transparent data-[hover=true]:bg-primary-50 data-[active=true]:bg-transparent",
		},
	],
});

const buttonTextStyle = tva({
	base: "web:select-none text-neutral-950 font-sans-semibold web:font-semibold text-center",
	parentVariants: {
		variant: {
			link: "underline data-[hover=true]:no-underline data-[active=true]:no-underline",
			outline: "",
			solid: "",
		},
		action: {
			primary: "",
			secondary: "",
			inverse: "text-neutral-0",
		},
		size: {
			xs: "text-xs",
			sm: "text-sm",
			md: "text-base",
			lg: "text-base",
			xl: "text-xl",
		},
	},
});

const buttonIconStyle = tva({
	base: "fill-none text-neutral-950",
	parentVariants: {
		variant: {
			link: "data-[hover=true]:underline data-[active=true]:underline",
		},
		size: {
			xs: "h-3.5 w-3.5",
			sm: "h-4 w-4",
			md: "h-[18px] w-[18px]",
			lg: "h-[18px] w-[18px]",
			xl: "h-5 w-5",
		},
	},
});

const buttonGroupStyle = tva({
	base: "",
	variants: {
		space: {
			xs: "gap-1",
			sm: "gap-2",
			md: "gap-3",
			lg: "gap-4",
			xl: "gap-5",
			"2xl": "gap-6",
			"3xl": "gap-7",
			"4xl": "gap-8",
		},
		isAttached: {
			true: "gap-0",
		},
		flexDirection: {
			row: "flex-row",
			column: "flex-col",
			"row-reverse": "flex-row-reverse",
			"column-reverse": "flex-col-reverse",
		},
	},
});

export type IButtonProps = Omit<React.ComponentPropsWithoutRef<typeof UIButton>, "context"> &
	VariantProps<typeof buttonStyle> & { className?: string };

const Button = React.forwardRef<React.ComponentRef<typeof UIButton>, IButtonProps>(
	({ className, variant = "solid", size = "md", action = "primary", disabled = false, ...props }, ref) => {
		return (
			<UIButton
				ref={ref}
				disabled={disabled}
				{...props}
				className={buttonStyle({
					variant,
					size,
					action,
					disabled,
					class: className,
				})}
				context={{ variant, size, action }}
			/>
		);
	},
);

type IButtonTextProps = React.ComponentPropsWithoutRef<typeof UIButton.Text> &
	VariantProps<typeof buttonTextStyle> & { className?: string };

const ButtonText = React.forwardRef<React.ComponentRef<typeof UIButton.Text>, IButtonTextProps>(
	({ className, variant, size, ...props }, ref) => {
		const { variant: parentVariant, size: parentSize, action: parentAction } = useStyleContext(SCOPE);

		return (
			<UIButton.Text
				ref={ref}
				{...props}
				className={buttonTextStyle({
					parentVariants: {
						variant: parentVariant,
						action: parentAction,
						size: parentSize,
					},
					variant,
					size,
					class: className,
				})}
			/>
		);
	},
);

const ButtonSpinner = UIButton.Spinner;

type IButtonIcon = React.ComponentPropsWithoutRef<typeof UIButton.Icon> &
	VariantProps<typeof buttonIconStyle> & {
		className?: string | undefined;
		as?: React.ElementType;
		height?: number;
		width?: number;
	};

const ButtonIcon = React.forwardRef<React.ComponentRef<typeof UIButton.Icon>, IButtonIcon>(
	({ className, size, ...props }, ref) => {
		const { variant: parentVariant, size: parentSize } = useStyleContext(SCOPE);

		if (typeof size === "number") {
			return <UIButton.Icon ref={ref} {...props} className={buttonIconStyle({ class: className })} size={size} />;
		}
		if ((props.height !== undefined || props.width !== undefined) && size === undefined) {
			return <UIButton.Icon ref={ref} {...props} className={buttonIconStyle({ class: className })} />;
		}
		return (
			<UIButton.Icon
				{...props}
				className={buttonIconStyle({
					parentVariants: {
						size: parentSize,
						variant: parentVariant,
					},
					size,
					class: className,
				})}
				ref={ref}
			/>
		);
	},
);

export type IButtonGroupProps = React.ComponentPropsWithoutRef<typeof UIButton.Group> &
	VariantProps<typeof buttonGroupStyle>;

const ButtonGroup = React.forwardRef<React.ComponentRef<typeof UIButton.Group>, IButtonGroupProps>(
	({ className, space = "md", isAttached = false, flexDirection = "column", ...props }, ref) => {
		return (
			<UIButton.Group
				className={buttonGroupStyle({
					class: className,
					space,
					isAttached,
					flexDirection,
				})}
				{...props}
				ref={ref}
			/>
		);
	},
);

Button.displayName = "Button";
ButtonText.displayName = "ButtonText";
ButtonSpinner.displayName = "ButtonSpinner";
ButtonIcon.displayName = "ButtonIcon";
ButtonGroup.displayName = "ButtonGroup";

export { Button, ButtonGroup, ButtonIcon, ButtonSpinner, ButtonText };
